import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/content";
import { track } from "../lib/tracking";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="group flex flex-col leading-none">
    <span className="font-display text-lg sm:text-xl tracking-[0.22em] text-ivory transition-colors duration-300 group-hover:text-gold">
      CELEBRITY
    </span>
    <span className="mt-1 text-[10px] font-sans font-semibold uppercase tracking-[0.55em] text-gold">
      Bouncers
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "border-b border-gold/15 bg-obsidian/90 py-3 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent py-5"
        }`}
      >
        <div className="container-x flex items-center justify-between">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 hover:text-gold ${
                    isActive ? "text-gold" : "text-ivory/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={BRAND.phonePrimaryHref}
              data-testid="header-call-link"
              onClick={() => track("call_click", { placement: "header" })}
              className="hidden items-center gap-2 text-sm text-ivory/80 transition-colors duration-300 hover:text-gold md:flex"
            >
              <Phone size={14} className="text-gold" />
              <span className="tracking-wide">{BRAND.phonePrimary}</span>
            </a>
            <Link
              to="/contact"
              data-testid="header-request-security-btn"
              className="hidden border border-gold/60 px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian sm:block"
            >
              Request Security
            </Link>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="text-ivory lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] flex flex-col bg-obsidian/[0.985] backdrop-blur-2xl"
          >
            <div className="container-x flex items-center justify-between py-5">
              <Logo />
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="text-ivory"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="container-x mt-8 flex flex-1 flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-serif text-3xl transition-colors duration-300 ${
                        isActive ? "text-gold" : "text-ivory hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="mt-8 flex flex-col gap-3"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-request-security-btn"
                  className="bg-gold px-6 py-4 text-center text-sm font-bold uppercase tracking-[0.2em] text-obsidian"
                >
                  Request Security
                </Link>
                <a
                  href={BRAND.phonePrimaryHref}
                  data-testid="mobile-call-link"
                  onClick={() => track("call_click", { placement: "menu" })}
                  className="border border-gold/40 px-6 py-4 text-center text-sm font-semibold uppercase tracking-[0.2em] text-gold"
                >
                  Call {BRAND.phonePrimary}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
